import { Copy, Eye, Share } from '@/components/icons';
import WhatsAppIcon from '@/components/icons/WhatsAppIcon';
import {
    DataTable,
    DataTableBody,
    DataTableCell,
    DataTableHead,
    DataTableHeader,
    DataTableHeaderRow,
    DataTableRow,
    EmptyState,
} from '@/components/system';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { formatDateTime } from './formatters';
import { shareSurveyLink } from './shareSurveyLink';
import { SurveyStatusBadge } from './StatusBadges';
import type { PatientSurveyOverview } from '@/types/patient-overview';
import type { ReactNode } from 'react';

type Props = {
    patientId: string;
    surveys: PatientSurveyOverview[];
    onViewAnswers: (reportId: string) => void;
    emptyAction?: ReactNode;
};

export default function PatientSurveysTable({
    patientId,
    surveys,
    onViewAnswers,
    emptyAction,
}: Props) {
    const handleShare = (assignmentId: string, action: 'copy' | 'whatsapp') =>
        shareSurveyLink({
            patientId,
            assignmentId,
            action,
        });

    if (surveys.length === 0) {
        return <EmptyState title="Nenhum formulário enviado" action={emptyAction} />;
    }

    return (
        <DataTable>
            <DataTableHeader>
                <DataTableHeaderRow>
                    <DataTableHead>Formulário</DataTableHead>
                    <DataTableHead>Status</DataTableHead>
                    <DataTableHead>Enviado em</DataTableHead>
                    <DataTableHead>Respondido em</DataTableHead>
                    <DataTableHead>Ações</DataTableHead>
                </DataTableHeaderRow>
            </DataTableHeader>
            <DataTableBody>
                {surveys.map((survey) => (
                    <DataTableRow key={survey.id}>
                        <DataTableCell>
                            <span className="font-medium text-foreground">
                                {survey.survey_name}
                            </span>
                        </DataTableCell>
                        <DataTableCell>
                            <SurveyStatusBadge status={survey.status} />
                        </DataTableCell>
                        <DataTableCell variant="subtle">
                            {formatDateTime(survey.created_at)}
                        </DataTableCell>
                        <DataTableCell variant="subtle">
                            {formatDateTime(survey.answered_at)}
                        </DataTableCell>
                        <DataTableCell>
                            {survey.status === 'COMPLETED' ? (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => onViewAnswers(survey.id)}
                                >
                                    <Eye />
                                    Visualizar respostas
                                </Button>
                            ) : (
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="text-muted-foreground hover:bg-brand-subtle hover:text-brand"
                                        >
                                            <Share />
                                            Compartilhar
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                        <DropdownMenuItem
                                            onClick={() => handleShare(survey.id, 'copy')}
                                            className="cursor-pointer"
                                        >
                                            <Copy className="mr-2 size-4" />
                                            Copiar link
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                            onClick={() => handleShare(survey.id, 'whatsapp')}
                                            className="cursor-pointer"
                                        >
                                            <WhatsAppIcon className="mr-2 size-4" />
                                            Compartilhar no WhatsApp
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            )}
                        </DataTableCell>
                    </DataTableRow>
                ))}
            </DataTableBody>
        </DataTable>
    );
}
